import { Controller } from "@hotwired/stimulus"

// Typeahead: debounced fetch to /search/suggest?q=, renders a menu,
// supports keyboard navigation (up/down/enter/esc)
export default class extends Controller {
  static targets = ["input", "menu"]

  connect() {
    this.debounceTimer = null
    this.activeIndex = -1
  }

  onInput(event) {
    const term = event.target.value.trim()
    this.activeIndex = -1
    if (!term) {
      this.menuTarget.innerHTML = ""
      this.hideMenu()
      return
    }
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.fetch(term), 200)
  }

  onFocus() {
    if (this.menuTarget.innerHTML.trim() !== "") this.showMenu()
  }

  onBlur() {
    setTimeout(() => this.hideMenu(), 150)
  }

  onKeydown(event) {
    const items = Array.from(this.menuTarget.querySelectorAll('[data-role="suggest-item"]'))
    if (!items.length) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.activeIndex = (this.activeIndex + 1) % items.length
        this.highlight(items)
        break
      case "ArrowUp":
        event.preventDefault()
        this.activeIndex = (this.activeIndex - 1 + items.length) % items.length
        this.highlight(items)
        break
      case "Enter":
        if (this.activeIndex >= 0) {
          event.preventDefault()
          const url = items[this.activeIndex].getAttribute("data-url")
          if (url) window.location.assign(url)
        }
        break
      case "Escape":
        this.hideMenu()
        break
    }
  }

  showMenu() {
    this.menuTarget.style.display = "block"
    this.menuTarget.classList.add("show")
  }

  hideMenu() {
    this.menuTarget.style.display = "none"
    this.menuTarget.classList.remove("show")
  }

  highlight(items) {
    items.forEach((el, i) => {
      if (i === this.activeIndex) el.classList.add("active")
      else el.classList.remove("active")
    })
  }

  async fetch(term) {
    try {
      const url = `/search/suggest?q=${encodeURIComponent(term)}`
      const res = await fetch(url, { headers: { Accept: "application/json" } })
      if (!res.ok) return
      const json = await res.json()
      this.render(json.results || [])
    } catch {
      // noop
    }
  }

  render(results) {
    if (!results.length) {
      this.menuTarget.innerHTML = ""
      this.hideMenu()
      return
    }

    const items = results
      .slice(0, 10)
      .map((r) => {
        const emoji = r.type === "person" ? "👤" : "🏢"
        const sub = r.sublabel ? `<div class="small text-muted mt-1">${this.escape(r.sublabel)}</div>` : ""
        return `
          <button type="button" class="list-group-item list-group-item-action d-block text-start" data-role="suggest-item" data-url="${this.escapeAttr(r.url)}" data-action="click->search-suggest#navigate">
            <span class="me-2">${emoji}</span>
            <span>${this.escape(r.label)}</span>
            ${sub}
          </button>
        `
      })
      .join("")

    this.menuTarget.innerHTML = `<div class="list-group">${items}</div>`
    this.showMenu()
  }

  navigate(event) {
    const url = event.currentTarget.getAttribute("data-url")
    if (url) window.location.assign(url)
  }

  escape(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    }[c]))
  }

  escapeAttr(str) {
    return this.escape(str)
  }
}
